import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AddRounded as AddIcon,
  AutoAwesomeRounded as AutoIcon,
  CategoryRounded as ItemIcon,
  CheckRounded as CheckIcon,
  CloseRounded as CloseIcon,
  DeleteOutlineRounded as DeleteIcon,
  DiamondRounded as DiamondIcon,
  EditRounded as EditIcon,
  RestoreRounded as RestoreIcon,
  SearchRounded as SearchIcon,
  StorefrontRounded as BranchIcon,
  WarningAmberRounded as WarnIcon,
} from "@mui/icons-material";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { apiErrorMessage } from "../../utils/apiError";
import { branchLabelFor } from "../../utils/branchDisplay";
import { reportSwitcherStripSx } from "./reportLayout";
import {
  API_BASE_URL,
  CODE_MAX,
  NAME_MAX,
  createItemType,
  deleteItemType,
  fetchItemTypes,
  normalizeItemName,
  normalizeShortCode,
  restoreItemType,
  suggestShortCode,
  takenCodesIn,
  updateItemType,
} from "./itemTypes";

/* ─── Blue/White Design Tokens (shared with the pawn master screens) ─────── */
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_LIGHT = "#DBEAFE";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const BG_SOFT = "#F8FAFC";
const SUCCESS = "#059669";
const DANGER = "#DC2626";
const DANGER_BG = "#FEF2F2";
const WARN = "#B45309";
const WARN_BG = "#FFFBEB";

const hiddenScrollbarSx = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

// Compact field, pawn look. `tone` paints the outline for validation:
// "error" red, "warn" amber, otherwise blue on focus.
const fieldSx = (tone) => {
  const c = tone === "error" ? DANGER : tone === "warn" ? WARN : null;
  return {
    "& .MuiOutlinedInput-root": {
      bgcolor: tone === "error" ? DANGER_BG : SURFACE,
      borderRadius: 2,
      fontSize: "0.85rem",
      color: INK,
      "& fieldset": { borderColor: c || BORDER },
      "&:hover fieldset": { borderColor: c || BLUE },
      "&.Mui-focused fieldset": { borderColor: c || BLUE, borderWidth: "1.5px" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.82rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: c || BLUE },
    "& input::placeholder": { color: INK_MUTED, opacity: 0.8 },
    "& .MuiFormHelperText-root": {
      mx: 0.25,
      mt: 0.4,
      fontSize: "0.68rem",
      fontWeight: 600,
      color: c || INK_MUTED,
    },
  };
};

const monoInputSx = {
  "& input": {
    fontFamily: "monospace",
    fontWeight: 700,
    letterSpacing: "0.06em",
  },
};

const primaryBtnSx = {
  bgcolor: BLUE,
  textTransform: "none",
  fontWeight: 700,
  boxShadow: "none",
  borderRadius: 2,
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: BLUE, color: "#fff", opacity: 0.55 },
};

const CodeChip = ({ code, muted }) => (
  <Chip
    label={code}
    size="small"
    sx={{
      height: 20,
      fontSize: "0.66rem",
      fontWeight: 800,
      fontFamily: "monospace",
      letterSpacing: "0.05em",
      bgcolor: muted ? "#F1F5F9" : BLUE_PALE,
      color: muted ? INK_MUTED : BLUE_DARK,
      border: `1px solid ${muted ? BORDER : BLUE_LIGHT}`,
    }}
  />
);

/* ─── What a typed name/code would collide with ──────────────────────────────
 * Mirrors the two DB unique keys so the user hears about a clash while typing,
 * not after a round trip (see feedback: FE mandatory-field validation).
 *   name — (metal, name), deleted rows included. A deleted match is not an
 *          error on ADD: the server restores that row, so it is a heads-up.
 *   code — (store, branch, code), deleted rows included.
 * ─────────────────────────────────────────────────────────────────────────── */
const nameClashOf = (itemTypes, metalId, name, exceptId) => {
  const n = normalizeItemName(name);
  if (!n) return null;
  return (
    itemTypes.find(
      (t) =>
        Number(t.metal_type_id) === Number(metalId) &&
        t.id !== exceptId &&
        normalizeItemName(t.name) === n,
    ) || null
  );
};

const codeClashOf = (itemTypes, branchId, code, exceptId) => {
  const c = normalizeShortCode(code);
  if (!c) return null;
  return (
    itemTypes.find(
      (t) =>
        (branchId == null || Number(t.branch_id) === Number(branchId)) &&
        t.id !== exceptId &&
        String(t.short_code).toUpperCase() === c,
    ) || null
  );
};

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
 * Master-detail: pick a metal, then manage what pieces of that metal are
 * pledged as. Side by side from `lg`; below that the metals become a strip of
 * chips above the list, so a phone never splits one screen into two half-
 * useful panels.
 *
 * The list is STORE-wide (with each row's branch shown on a multi-branch
 * store): the DB keeps one CHAIN per metal across every branch, so a branch-
 * only list would hide the very row that refuses another branch's "duplicate".
 * ─────────────────────────────────────────────────────────────────────────── */
export default function PawnItemTypeMaster({ embedded = false }) {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));

  const { currentStoreInfo, userInfo, branchScope } =
    useContext(AutoTaskAuthContext) || {};
  const loggedUser =
    JSON.parse(localStorage.getItem("CapacitorStorage.user") || "null") || {};
  const storeFromLS =
    JSON.parse(
      localStorage.getItem("CapacitorStorage.current_store_info") || "null",
    ) || {};
  const storeInfo = currentStoreInfo || storeFromLS;
  const store_id =
    currentStoreInfo?.id ??
    storeFromLS?.id ??
    userInfo?.store_id ??
    loggedUser?.store_id;
  const user_id = userInfo?.id ?? loggedUser?.id;

  // The branch a new item type is created at. In the all-branches view (or for
  // a role with no branch) nothing is sent and the server uses the main branch.
  const writeBranchId =
    branchScope?.mode === "branch" && branchScope?.branchId != null
      ? branchScope.branchId
      : null;
  const branchCount = Number(
    storeInfo?.branch_count ?? storeInfo?.branches?.length ?? 0,
  );

  const [metals, setMetals] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [selectedMetalId, setSelectedMetalId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");

  // Quick-add bar. The code follows the name (suggested) until the user types
  // in it themselves — then it is theirs.
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [addErrors, setAddErrors] = useState({});
  const [adding, setAdding] = useState(false);
  const nameInputRef = useRef(null);

  // Inline edit: one row at a time.
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editErrors, setEditErrors] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  /* ── Loading ─────────────────────────────────────────────────────────── */
  const loadItemTypes = useCallback(async () => {
    if (store_id == null) return;
    const { rows } = await fetchItemTypes(store_id, { status: "all" });
    setItemTypes(rows);
  }, [store_id]);

  useEffect(() => {
    if (store_id == null) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [metalRes] = await Promise.all([
          axios.get(
            `${API_BASE_URL}/api/jewel-master/metal-type-master/store/${store_id}`,
          ),
          loadItemTypes(),
        ]);
        if (!alive) return;
        // Same rule as the Payment screen's metal dropdown: only metals a
        // teller can actually pick there are worth managing items for.
        const list = (metalRes.data?.data || []).filter(
          (m) => m.is_active && !m.is_deleted,
        );
        setMetals(list);
        setSelectedMetalId((prev) =>
          list.some((m) => m.id === prev) ? prev : list[0]?.id ?? null,
        );
      } catch (err) {
        if (alive) notify(apiErrorMessage(err, "Failed to load item types"), "error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [store_id, loadItemTypes]);

  const selectedMetal = metals.find((m) => m.id === selectedMetalId) || null;

  const countsByMetal = useMemo(() => {
    const out = {};
    itemTypes.forEach((t) => {
      if (!out[t.metal_type_id]) out[t.metal_type_id] = { active: 0, deleted: 0 };
      const c = out[t.metal_type_id];
      if (t.is_active) c.active += 1;
      else c.deleted += 1;
    });
    return out;
  }, [itemTypes]);
  const metalCounts = countsByMetal[selectedMetalId] || { active: 0, deleted: 0 };

  const visibleRows = useMemo(() => {
    const q = search.trim().toUpperCase();
    return itemTypes
      .filter((t) => Number(t.metal_type_id) === Number(selectedMetalId))
      .filter((t) => (view === "active" ? t.is_active : !t.is_active))
      .filter(
        (t) =>
          !q ||
          String(t.name).toUpperCase().includes(q) ||
          String(t.short_code).toUpperCase().includes(q),
      );
  }, [itemTypes, selectedMetalId, view, search]);

  // Switching metal starts a clean slate: no half-typed name carried over to
  // the wrong metal, no row left in edit mode.
  useEffect(() => {
    setNewName("");
    setNewCode("");
    setCodeTouched(false);
    setAddErrors({});
    setEditId(null);
    setSearch("");
  }, [selectedMetalId]);

  /* ── Quick add ───────────────────────────────────────────────────────── */
  const suggestedCode = useMemo(
    () => suggestShortCode(newName, takenCodesIn(itemTypes, writeBranchId)),
    [newName, itemTypes, writeBranchId],
  );
  const effectiveCode = codeTouched ? newCode : suggestedCode;

  const addNameClash = nameClashOf(itemTypes, selectedMetalId, newName);
  // A deleted name restores in place, keeping its own code and branch — so
  // its code is the one that matters, not the suggestion.
  const restoring = addNameClash && !addNameClash.is_active ? addNameClash : null;
  const addCodeClash = restoring
    ? null
    : codeClashOf(itemTypes, writeBranchId, effectiveCode);

  const nameHint = addErrors.name
    ? { tone: "error", text: addErrors.name }
    : addNameClash?.is_active
    ? { tone: "error", text: `${addNameClash.name} already exists` }
    : restoring
    ? { tone: "warn", text: "Deleted earlier — adding restores it" }
    : null;
  const codeHint = addErrors.code
    ? { tone: "error", text: addErrors.code }
    : addCodeClash
    ? {
        tone: "error",
        text: `Used by ${addCodeClash.name}${addCodeClash.is_active ? "" : " (deleted)"}`,
      }
    : null;

  const handleAdd = async () => {
    if (!selectedMetal || adding) return;
    const errs = {};
    if (!normalizeItemName(newName)) errs.name = "Item type name is required";
    else if (addNameClash?.is_active) errs.name = `${addNameClash.name} already exists`;
    if (!restoring) {
      if (!normalizeShortCode(effectiveCode)) errs.code = "Short code is required";
      else if (addCodeClash) errs.code = `Used by ${addCodeClash.name}`;
    }
    if (Object.keys(errs).length) {
      setAddErrors(errs);
      notify(
        errs.name === "Item type name is required" || errs.code === "Short code is required"
          ? "Please fill all mandatory fields"
          : Object.values(errs)[0],
        "warning",
      );
      nameInputRef.current?.focus();
      return;
    }

    setAdding(true);
    try {
      const res = await createItemType(store_id, {
        metal_type_id: selectedMetal.id,
        name: newName,
        // A restore keeps the row's own code unless the user typed another.
        ...(restoring && !codeTouched ? {} : { short_code: effectiveCode }),
        ...(writeBranchId != null ? { branch_id: writeBranchId } : {}),
        user_id,
      });
      await loadItemTypes();
      setNewName("");
      setNewCode("");
      setCodeTouched(false);
      setAddErrors({});
      setView("active");
      notify(res?.message || "Item type added", "success");
      // Straight back to the name for the next one — shops key these in runs.
      setTimeout(() => nameInputRef.current?.focus(), 0);
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to add item type"), "error");
    } finally {
      setAdding(false);
    }
  };

  // Clear a flagged field as soon as it is filled.
  useEffect(() => {
    setAddErrors((prev) => {
      if (!prev.name && !prev.code) return prev;
      const next = { ...prev };
      if (next.name && normalizeItemName(newName)) delete next.name;
      if (next.code && normalizeShortCode(effectiveCode)) delete next.code;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [newName, effectiveCode]);

  /* ── Inline edit ─────────────────────────────────────────────────────── */
  const startEdit = (row) => {
    setEditId(row.id);
    setEditName(row.name);
    setEditCode(row.short_code);
    setEditErrors({});
  };
  const cancelEdit = () => {
    setEditId(null);
    setEditErrors({});
  };

  const saveEdit = async (row) => {
    const errs = {};
    const nameClash = nameClashOf(itemTypes, row.metal_type_id, editName, row.id);
    const codeClash = codeClashOf(itemTypes, row.branch_id, editCode, row.id);
    if (!normalizeItemName(editName)) errs.name = "Required";
    else if (nameClash)
      errs.name = nameClash.is_active
        ? `${nameClash.name} already exists`
        : `A deleted item is called ${nameClash.name}`;
    if (!normalizeShortCode(editCode)) errs.code = "Required";
    else if (codeClash) errs.code = `Used by ${codeClash.name}`;
    if (Object.keys(errs).length) {
      setEditErrors(errs);
      notify(
        errs.name === "Required" || errs.code === "Required"
          ? "Please fill all mandatory fields"
          : Object.values(errs)[0],
        "warning",
      );
      return;
    }
    if (
      normalizeItemName(editName) === row.name &&
      normalizeShortCode(editCode) === row.short_code
    ) {
      cancelEdit();
      return;
    }
    setSavingEdit(true);
    try {
      const res = await updateItemType(store_id, row.id, {
        name: editName,
        short_code: editCode,
        user_id,
      });
      await loadItemTypes();
      cancelEdit();
      notify(res?.message || "Item type updated", "success");
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to update item type"), "error");
    } finally {
      setSavingEdit(false);
    }
  };

  /* ── Delete / restore ────────────────────────────────────────────────── */
  const doDelete = async () => {
    const row = confirmDelete;
    if (!row) return;
    setBusyId(row.id);
    try {
      const res = await deleteItemType(store_id, row.id, user_id);
      await loadItemTypes();
      setConfirmDelete(null);
      notify(res?.message || "Item type deleted", "success");
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to delete item type"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const doRestore = async (row) => {
    setBusyId(row.id);
    try {
      const res = await restoreItemType(store_id, row.id, user_id);
      await loadItemTypes();
      notify(res?.message || "Item type restored", "success");
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to restore item type"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const branchLabelOf = (row) =>
    branchLabelFor({
      branchName: row.branch_name,
      storeName: storeInfo?.name,
      branchCount,
    });

  /* ── Render pieces ───────────────────────────────────────────────────── */
  const outerSx = embedded
    ? {
        p: 0,
        minWidth: 0,
        flex: { xs: "0 0 auto", lg: 1 },
        minHeight: { xs: "auto", lg: 0 },
        display: "flex",
        flexDirection: "column",
      }
    : {
        height: "100%",
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: "#F1F5F9",
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
        display: "flex",
        flexDirection: "column",
      };

  const metalRail = (
    <Card
      elevation={0}
      sx={{
        width: 300,
        flexShrink: 0,
        border: `1px solid ${BORDER}`,
        borderRadius: 3,
        bgcolor: SURFACE,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: INK, fontSize: "0.95rem" }}>
          Metals
        </Typography>
        <Chip
          label={metals.length}
          size="small"
          sx={{
            height: 20,
            bgcolor: BLUE_PALE,
            color: BLUE_DARK,
            fontWeight: 700,
            fontSize: "0.7rem",
          }}
        />
      </Box>
      <Typography
        sx={{
          px: 2,
          py: 1,
          fontSize: "0.7rem",
          color: INK_MUTED,
          bgcolor: BG_SOFT,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        Pick a metal to manage what its pieces are pledged as.
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...hiddenScrollbarSx }}>
        {metals.map((m) => {
          const selected = m.id === selectedMetalId;
          const n = countsByMetal[m.id]?.active || 0;
          return (
            <Box
              key={m.id}
              onClick={() => setSelectedMetalId(m.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 1.25,
                cursor: "pointer",
                borderBottom: `1px solid ${BORDER}`,
                borderLeft: `3px solid ${selected ? BLUE : "transparent"}`,
                bgcolor: selected ? BLUE_PALE : "transparent",
                transition: "background-color 120ms",
                "&:hover": { bgcolor: BLUE_PALE },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: selected ? BLUE : BLUE_PALE,
                  color: selected ? "#fff" : BLUE_DARK,
                  boxShadow: selected ? "0 4px 10px rgba(37,99,235,0.3)" : "none",
                  transition: "all 150ms",
                }}
              >
                <DiamondIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    noWrap
                    sx={{ fontWeight: 700, fontSize: "0.86rem", color: INK }}
                  >
                    {m.name}
                  </Typography>
                  {m.code && <CodeChip code={m.code} muted={!selected} />}
                </Box>
                <Typography noWrap sx={{ fontSize: "0.7rem", color: INK_MUTED }}>
                  {n === 0 ? "No item types yet" : `${n} item type${n === 1 ? "" : "s"}`}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Card>
  );

  // Below lg: the metals as a sideways-scrolling chip strip.
  const metalStrip = (
    <Box sx={{ ...reportSwitcherStripSx, mb: 1.5 }}>
      <Box sx={{ display: "flex", gap: 1, width: "max-content", pb: 0.25 }}>
        {metals.map((m) => {
          const selected = m.id === selectedMetalId;
          const n = countsByMetal[m.id]?.active || 0;
          return (
            <Box
              key={m.id}
              component="button"
              type="button"
              onClick={() => setSelectedMetalId(m.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                border: `1px solid ${selected ? BLUE : BORDER}`,
                bgcolor: selected ? BLUE : SURFACE,
                color: selected ? "#fff" : INK_SOFT,
                font: "inherit",
                fontSize: "0.8rem",
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: selected ? "0 4px 10px rgba(37,99,235,0.25)" : "none",
                transition: "all 150ms",
              }}
            >
              <DiamondIcon sx={{ fontSize: 16 }} />
              {m.name}
              <Box
                component="span"
                sx={{
                  minWidth: 20,
                  px: 0.6,
                  borderRadius: 999,
                  fontSize: "0.68rem",
                  bgcolor: selected ? "rgba(255,255,255,0.22)" : BLUE_PALE,
                  color: selected ? "#fff" : BLUE_DARK,
                }}
              >
                {n}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const quickAdd = selectedMetal && (
    <Box
      sx={{
        mx: { xs: 1.5, sm: 2 },
        mt: 1.5,
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: 2.5,
        bgcolor: BLUE_PALE,
        border: `1px solid ${BLUE_LIGHT}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 800,
          color: BLUE_DARK,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          mb: 1,
        }}
      >
        New {selectedMetal.name} item type
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: 1,
        }}
      >
        <TextField
          inputRef={nameInputRef}
          size="small"
          label="Item type name"
          required
          placeholder="e.g. CHAIN"
          value={newName}
          onChange={(e) => setNewName(e.target.value.toUpperCase().slice(0, NAME_MAX))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          error={nameHint?.tone === "error"}
          helperText={nameHint?.text || " "}
          sx={{ flex: 2, minWidth: 0, ...fieldSx(nameHint?.tone) }}
          inputProps={{ autoComplete: "off" }}
        />
        <TextField
          size="small"
          label="Short code"
          required
          placeholder="Auto"
          value={restoring && !codeTouched ? restoring.short_code : effectiveCode}
          onChange={(e) => {
            setCodeTouched(true);
            setNewCode(normalizeShortCode(e.target.value));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          error={codeHint?.tone === "error"}
          helperText={
            codeHint?.text ||
            (restoring && !codeTouched
              ? "Kept from the deleted item"
              : !codeTouched && effectiveCode
              ? "Suggested — type to change"
              : `Letters & digits, up to ${CODE_MAX}`)
          }
          sx={{ flex: 1, minWidth: { sm: 150 }, ...fieldSx(codeHint?.tone), ...monoInputSx }}
          inputProps={{ autoComplete: "off", maxLength: CODE_MAX }}
          InputProps={{
            endAdornment:
              !codeTouched && effectiveCode && !restoring ? (
                <InputAdornment position="end">
                  <Tooltip title="Suggested from the name">
                    <AutoIcon sx={{ fontSize: 16, color: BLUE }} />
                  </Tooltip>
                </InputAdornment>
              ) : null,
          }}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={adding}
          startIcon={
            adding ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : restoring ? (
              <RestoreIcon />
            ) : (
              <AddIcon />
            )
          }
          sx={{ ...primaryBtnSx, height: 40, px: 2.25, flexShrink: 0 }}
        >
          {restoring ? "Restore" : "Add"}
        </Button>
      </Box>
    </Box>
  );

  const toolbar = selectedMetal && (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1,
      }}
    >
      <TextField
        size="small"
        placeholder="Search name or code…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ flex: 1, ...fieldSx() }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: INK_MUTED, fontSize: 18 }} />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch("")}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <ToggleButtonGroup
        value={view}
        exclusive
        size="small"
        onChange={(_e, v) => v && setView(v)}
        sx={{
          flexShrink: 0,
          "& .MuiToggleButton-root": {
            textTransform: "none",
            px: 1.5,
            py: 0.6,
            fontSize: "0.76rem",
            fontWeight: 700,
            color: INK_MUTED,
            borderColor: BORDER,
            bgcolor: SURFACE,
            gap: 0.6,
            flex: { xs: 1, sm: "0 0 auto" },
          },
          "& .Mui-selected": {
            bgcolor: `${BLUE} !important`,
            color: "#fff !important",
          },
        }}
      >
        <ToggleButton value="active">Active · {metalCounts.active}</ToggleButton>
        <ToggleButton value="deleted">Deleted · {metalCounts.deleted}</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const emptyState = (
    <Box sx={{ textAlign: "center", py: 6, px: 3 }}>
      <Box
        sx={{
          width: 64,
          height: 64,
          mx: "auto",
          mb: 1.5,
          borderRadius: "50%",
          bgcolor: BLUE_PALE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {view === "deleted" ? (
          <RestoreIcon sx={{ fontSize: 30, color: BLUE }} />
        ) : (
          <ItemIcon sx={{ fontSize: 30, color: BLUE }} />
        )}
      </Box>
      <Typography sx={{ fontWeight: 700, color: INK, fontSize: "0.92rem" }}>
        {search
          ? "Nothing matches that search"
          : view === "deleted"
          ? "Nothing deleted"
          : `No ${selectedMetal?.name || ""} item types yet`}
      </Typography>
      <Typography sx={{ color: INK_MUTED, fontSize: "0.78rem", mt: 0.5 }}>
        {search
          ? "Try part of the name, or the short code."
          : view === "deleted"
          ? "Deleted item types land here and can be restored any time."
          : "Add CHAIN, BANGLE, RING… above — they appear in the Payment screen's item list straight away."}
      </Typography>
    </Box>
  );

  const renderRow = (row) => {
    const editing = editId === row.id;
    const branchLabel = branchLabelOf(row);
    const busy = busyId === row.id;

    if (editing) {
      const nameErr = editErrors.name;
      const codeErr = editErrors.code;
      return (
        <Box
          key={row.id}
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.25,
            bgcolor: BLUE_PALE,
            borderBottom: `1px solid ${BORDER}`,
            borderLeft: `3px solid ${BLUE}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: 1,
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveEdit(row);
              } else if (e.key === "Escape") {
                cancelEdit();
              }
            }}
          >
            <TextField
              autoFocus
              size="small"
              label="Name"
              value={editName}
              onChange={(e) => {
                setEditName(e.target.value.toUpperCase().slice(0, NAME_MAX));
                if (nameErr) setEditErrors((p) => ({ ...p, name: undefined }));
              }}
              error={Boolean(nameErr)}
              helperText={nameErr || " "}
              sx={{ flex: 2, ...fieldSx(nameErr ? "error" : null) }}
              inputProps={{ autoComplete: "off" }}
            />
            <TextField
              size="small"
              label="Short code"
              value={editCode}
              onChange={(e) => {
                setEditCode(normalizeShortCode(e.target.value));
                if (codeErr) setEditErrors((p) => ({ ...p, code: undefined }));
              }}
              error={Boolean(codeErr)}
              helperText={codeErr || " "}
              sx={{ flex: 1, ...fieldSx(codeErr ? "error" : null), ...monoInputSx }}
              inputProps={{ autoComplete: "off", maxLength: CODE_MAX }}
            />
            <Box sx={{ display: "flex", gap: 0.75, pt: { sm: 0.25 } }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => saveEdit(row)}
                disabled={savingEdit}
                startIcon={
                  savingEdit ? (
                    <CircularProgress size={12} sx={{ color: "#fff" }} />
                  ) : (
                    <CheckIcon />
                  )
                }
                sx={{ ...primaryBtnSx, height: 36, flex: { xs: 1, sm: "0 0 auto" } }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={cancelEdit}
                disabled={savingEdit}
                sx={{
                  height: 36,
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  color: INK_SOFT,
                  borderColor: BORDER,
                  flex: { xs: 1, sm: "0 0 auto" },
                  "&:hover": { borderColor: BLUE, bgcolor: SURFACE },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
          {row.usage_count > 0 && (
            <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED, mt: 0.25 }}>
              Renaming also renames it on the {row.usage_count} pledged ornament
              {row.usage_count === 1 ? "" : "s"} that use it.
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box
        key={row.id}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 1.5, sm: 2 },
          py: 1.1,
          borderBottom: `1px solid ${BORDER}`,
          transition: "background-color 120ms",
          "&:hover": { bgcolor: BG_SOFT },
          "&:hover .row-actions": { opacity: 1 },
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: "0.9rem",
            bgcolor: row.is_active ? BLUE_PALE : "#F1F5F9",
            color: row.is_active ? BLUE_DARK : INK_MUTED,
          }}
        >
          {String(row.name).charAt(0)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
              rowGap: 0.25,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.86rem",
                color: row.is_active ? INK : INK_MUTED,
                textDecoration: row.is_active ? "none" : "line-through",
                textDecorationColor: "#CBD5E1",
                wordBreak: "break-word",
              }}
            >
              {row.name}
            </Typography>
            <CodeChip code={row.short_code} muted={!row.is_active} />
            {branchLabel && (
              <Chip
                icon={<BranchIcon sx={{ fontSize: "13px !important" }} />}
                label={branchLabel}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.64rem",
                  fontWeight: 600,
                  bgcolor: SURFACE,
                  color: INK_MUTED,
                  border: `1px solid ${BORDER}`,
                  "& .MuiChip-icon": { color: INK_MUTED, ml: 0.5 },
                }}
              />
            )}
          </Box>
          <Typography sx={{ fontSize: "0.7rem", color: INK_MUTED }}>
            {row.usage_count > 0
              ? `On ${row.usage_count} pledged ornament${row.usage_count === 1 ? "" : "s"}`
              : "Not pledged yet"}
          </Typography>
        </Box>

        {row.is_active ? (
          <Box
            className="row-actions"
            sx={{
              display: "flex",
              gap: 0.25,
              flexShrink: 0,
              // Quiet until hovered on a mouse; always there on touch.
              opacity: { xs: 1, md: 0.55 },
              transition: "opacity 120ms",
            }}
          >
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => startEdit(row)}
                sx={{ color: BLUE, "&:hover": { bgcolor: BLUE_PALE } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => setConfirmDelete(row)}
                sx={{ color: DANGER, "&:hover": { bgcolor: DANGER_BG } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => doRestore(row)}
            disabled={busy}
            startIcon={
              busy ? <CircularProgress size={12} sx={{ color: BLUE }} /> : <RestoreIcon />
            }
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              color: BLUE,
              borderColor: BLUE_LIGHT,
              "&:hover": { borderColor: BLUE, bgcolor: BLUE_PALE },
            }}
          >
            Restore
          </Button>
        )}
      </Box>
    );
  };

  const itemPanel = (
    <Card
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: { xs: "auto", lg: 0 },
        border: `1px solid ${BORDER}`,
        borderRadius: 3,
        bgcolor: SURFACE,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Head */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: BLUE,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
          }}
        >
          <ItemIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 800, color: INK, fontSize: "1rem" }}>
            {selectedMetal ? `${selectedMetal.name} item types` : "Item types"}
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.72rem", color: INK_MUTED }}>
            Offered in the Payment screen when {selectedMetal?.name || "this metal"} is
            the metal
          </Typography>
        </Box>
        {selectedMetal && (
          <Chip
            label={`${metalCounts.active} active`}
            size="small"
            sx={{
              height: 22,
              bgcolor: metalCounts.active ? "#ECFDF5" : "#F1F5F9",
              color: metalCounts.active ? SUCCESS : INK_MUTED,
              fontWeight: 700,
              fontSize: "0.7rem",
              flexShrink: 0,
            }}
          />
        )}
      </Box>

      {quickAdd}
      {toolbar}

      <Box
        sx={{
          flex: { xs: "0 0 auto", lg: 1 },
          minHeight: 0,
          overflowY: { lg: "auto" },
          borderTop: `1px solid ${BORDER}`,
          ...hiddenScrollbarSx,
        }}
      >
        {visibleRows.length === 0 ? emptyState : visibleRows.map(renderRow)}
      </Box>
    </Card>
  );

  return (
    <Box sx={outerSx}>
      {!embedded && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              borderRadius: 2,
              bgcolor: BLUE,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            <ItemIcon />
          </Box>
          <Typography
            sx={{ fontWeight: 800, fontSize: { xs: "1.05rem", sm: "1.25rem" }, color: INK }}
          >
            Item Type Master
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: BLUE }} />
        </Box>
      ) : metals.length === 0 ? (
        <Card
          elevation={0}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, py: 6, px: 3, textAlign: "center" }}
        >
          <DiamondIcon sx={{ fontSize: 44, color: BORDER, mb: 1 }} />
          <Typography sx={{ fontWeight: 700, color: INK }}>No active metals</Typography>
          <Typography sx={{ color: INK_MUTED, fontSize: "0.82rem", mt: 0.5 }}>
            Add a metal on the Metal Type tab first — item types belong to a metal.
          </Typography>
        </Card>
      ) : isLg ? (
        <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
          {metalRail}
          {itemPanel}
        </Box>
      ) : (
        <>
          {metalStrip}
          {itemPanel}
        </>
      )}

      {/* ── Delete confirmation ── */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => busyId == null && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: DANGER_BG,
                color: DANGER,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DeleteIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: INK, fontSize: "1rem" }}>
                Delete {confirmDelete?.name}?
              </Typography>
              <Typography sx={{ color: INK_SOFT, fontSize: "0.82rem", mt: 0.75 }}>
                It will no longer be offered for new pledges.
              </Typography>
              <Collapse in={(confirmDelete?.usage_count || 0) > 0}>
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: WARN_BG,
                    border: "1px solid #FDE68A",
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <WarnIcon sx={{ fontSize: 18, color: WARN, mt: "1px" }} />
                  <Typography sx={{ fontSize: "0.76rem", color: WARN }}>
                    {confirmDelete?.usage_count} pledged ornament
                    {confirmDelete?.usage_count === 1 ? " uses" : "s use"} it — they
                    keep showing {confirmDelete?.name} on tickets and reports.
                  </Typography>
                </Box>
              </Collapse>
              <Typography sx={{ color: INK_MUTED, fontSize: "0.74rem", mt: 1.25 }}>
                You can restore it any time from Deleted.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={busyId != null}
            sx={{ textTransform: "none", fontWeight: 700, color: INK_SOFT, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={doDelete}
            disabled={busyId != null}
            startIcon={
              busyId != null ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              boxShadow: "none",
              bgcolor: DANGER,
              "&:hover": { bgcolor: "#B91C1C", boxShadow: "none" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
